import React, { useState } from 'react';
import { milestoneAPI } from '../lib/api';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';
import { Plus, Target, Trash2, Edit, Diamond } from 'lucide-react';

export default function MilestonesManager({ projectId, tasks, milestones, onRefresh }) {
  const [addOpen, setAddOpen] = useState(false);
  const [editMilestone, setEditMilestone] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newMilestone, setNewMilestone] = useState({
    name: '',
    date: new Date().toISOString().split('T')[0],
    dependencies: [],
  });

  const handleAddMilestone = async (e) => {
    e.preventDefault();
    if (!newMilestone.name.trim()) {
      toast.error('Milestone name is required');
      return;
    }

    setLoading(true);
    try {
      await milestoneAPI.create(projectId, newMilestone);
      setAddOpen(false);
      setNewMilestone({
        name: '',
        date: new Date().toISOString().split('T')[0],
        dependencies: [],
      });
      onRefresh();
      toast.success('Milestone added');
    } catch (error) {
      toast.error('Failed to add milestone');
    } finally {
      setLoading(false);
    }
  };

  const handleEditMilestone = async (e) => {
    e.preventDefault();
    if (!editMilestone.name.trim()) {
      toast.error('Milestone name is required');
      return;
    }

    setLoading(true);
    try {
      await milestoneAPI.update(projectId, editMilestone.id, {
        name: editMilestone.name,
        date: editMilestone.date,
        dependencies: editMilestone.dependencies,
      });
      setEditMilestone(null);
      onRefresh();
      toast.success('Milestone updated');
    } catch (error) {
      toast.error('Failed to update milestone');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMilestone = async (milestoneId) => {
    if (!window.confirm('Delete this milestone?')) return;

    try {
      await milestoneAPI.delete(projectId, milestoneId);
      onRefresh();
      toast.success('Milestone deleted');
    } catch (error) {
      toast.error('Failed to delete milestone');
    }
  };

  const getDependencyNames = (deps) => {
    return deps?.map(depId => {
      const task = tasks.find(t => t.id === depId);
      return task ? task.name : 'Unknown';
    }).join(', ') || '-';
  };

  const toggleDependency = (taskId, milestone, setMilestone) => {
    const deps = milestone.dependencies || [];
    if (deps.includes(taskId)) {
      setMilestone({ ...milestone, dependencies: deps.filter(d => d !== taskId) });
    } else {
      setMilestone({ ...milestone, dependencies: [...deps, taskId] });
    }
  };

  return (
    <div className="space-y-6" data-testid="milestones-manager">
      <Card className="bg-white border-zinc-200 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-outfit font-semibold text-zinc-900">Project Milestones</h3>
            <p className="text-sm text-zinc-500 mt-1">
              Define key milestones and link them to task dependencies
            </p>
          </div>
          <Button
            onClick={() => setAddOpen(true)}
            data-testid="add-milestone-btn"
            className="bg-primary hover:bg-orange-600 text-white rounded-lg"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Milestone
          </Button>
        </div>

        {milestones.length === 0 ? (
          <div className="text-center py-12 text-zinc-500">
            <Target className="w-12 h-12 mx-auto mb-4 text-zinc-300" />
            <p>No milestones yet. Add milestones to track key project dates.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {milestones.map(milestone => {
              const depTasks = milestone.dependencies?.map(depId => 
                tasks.find(t => t.id === depId)
              ).filter(Boolean) || [];
              
              return (
                <Card
                  key={milestone.id}
                  className="bg-zinc-50 border-zinc-200 p-4 hover:shadow-md transition-shadow"
                  data-testid={`milestone-card-${milestone.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Diamond className="w-5 h-5 text-amber-500 fill-amber-500" />
                      <h4 className="font-medium text-zinc-900">{milestone.name}</h4>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditMilestone(milestone)}
                        data-testid={`edit-milestone-${milestone.id}`}
                        className="h-8 w-8 p-0"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteMilestone(milestone.id)}
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                        data-testid={`delete-milestone-${milestone.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm text-zinc-600 mb-3">
                    <span className="font-medium">Date:</span> {formatDate(milestone.date)}
                  </p>
                  {depTasks.length > 0 && (
                    <div>
                      <p className="text-xs text-zinc-500 font-medium mb-1">Dependencies:</p>
                      <div className="flex flex-wrap gap-1">
                        {depTasks.map(task => (
                          <span
                            key={task.id}
                            className="text-xs bg-white px-2 py-0.5 rounded border border-zinc-200"
                          >
                            {task.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </Card>

      {/* Milestones Table */}
      {milestones.length > 0 && (
        <Card className="bg-white border-zinc-200 rounded-xl p-6">
          <h3 className="font-outfit font-semibold text-zinc-900 mb-4">Milestones Table</h3>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Milestone Name</TableHead>
                  <TableHead>Target Date</TableHead>
                  <TableHead>Dependencies</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {milestones.map(milestone => (
                  <TableRow key={milestone.id} data-testid={`milestone-row-${milestone.id}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <Diamond className="w-4 h-4 text-amber-500 fill-amber-500" />
                        {milestone.name}
                      </div>
                    </TableCell>
                    <TableCell>{formatDate(milestone.date)}</TableCell>
                    <TableCell className="text-zinc-500">
                      {getDependencyNames(milestone.dependencies)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditMilestone(milestone)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteMilestone(milestone.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* Add Milestone Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-outfit">Add New Milestone</DialogTitle>
            <DialogDescription>Create a milestone to track project progress</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddMilestone} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Milestone Name *</Label>
              <Input
                value={newMilestone.name}
                onChange={(e) => setNewMilestone({ ...newMilestone, name: e.target.value })}
                placeholder="e.g., Phase 1 Complete"
                data-testid="add-milestone-name-input"
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Target Date</Label>
              <Input
                type="date"
                value={newMilestone.date}
                onChange={(e) => setNewMilestone({ ...newMilestone, date: e.target.value })}
                data-testid="add-milestone-date-input"
                className="rounded-xl"
              />
            </div>
            {tasks.length > 0 && (
              <div className="space-y-2">
                <Label>Dependencies (tasks that must complete)</Label>
                <div className="max-h-40 overflow-y-auto border border-zinc-200 rounded-xl p-3 space-y-2">
                  {tasks.map(task => (
                    <label
                      key={task.id}
                      className="flex items-center gap-2 cursor-pointer hover:bg-zinc-50 p-1 rounded"
                    >
                      <input
                        type="checkbox"
                        checked={newMilestone.dependencies?.includes(task.id) || false}
                        onChange={() => toggleDependency(task.id, newMilestone, setNewMilestone)}
                        className="rounded border-zinc-300"
                      />
                      <span className="text-sm text-zinc-700">{task.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)} className="rounded-lg">
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                data-testid="add-milestone-submit-btn"
                className="bg-primary hover:bg-orange-600 text-white rounded-lg"
              >
                {loading ? <span className="spinner" /> : 'Add Milestone'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Milestone Dialog */}
      <Dialog open={!!editMilestone} onOpenChange={() => setEditMilestone(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-outfit">Edit Milestone</DialogTitle>
            <DialogDescription>Update milestone details and dependencies</DialogDescription>
          </DialogHeader>
          {editMilestone && (
            <form onSubmit={handleEditMilestone} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Milestone Name *</Label>
                <Input
                  value={editMilestone.name}
                  onChange={(e) => setEditMilestone({ ...editMilestone, name: e.target.value })}
                  data-testid="edit-milestone-name-input"
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label>Target Date</Label>
                <Input
                  type="date"
                  value={editMilestone.date}
                  onChange={(e) => setEditMilestone({ ...editMilestone, date: e.target.value })}
                  data-testid="edit-milestone-date-input"
                  className="rounded-xl"
                />
              </div>
              {tasks.length > 0 && (
                <div className="space-y-2">
                  <Label>Dependencies</Label>
                  <div className="max-h-40 overflow-y-auto border border-zinc-200 rounded-xl p-3 space-y-2">
                    {tasks.map(task => (
                      <label
                        key={task.id}
                        className="flex items-center gap-2 cursor-pointer hover:bg-zinc-50 p-1 rounded"
                      >
                        <input
                          type="checkbox"
                          checked={editMilestone.dependencies?.includes(task.id) || false}
                          onChange={() => toggleDependency(task.id, editMilestone, setEditMilestone)}
                          className="rounded border-zinc-300"
                        />
                        <span className="text-sm text-zinc-700">{task.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditMilestone(null)} className="rounded-lg">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="edit-milestone-submit-btn"
                  className="bg-primary hover:bg-orange-600 text-white rounded-lg"
                >
                  {loading ? <span className="spinner" /> : 'Save Changes'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
